import { createI18n, type I18nInstance } from '@catawiki/cata-style';

const defaultLocale = 'en';
const sharedI18n = createI18n(defaultLocale);

type InitialI18nStore = Record<
  string,
  { translations?: Record<string, unknown> }
>;

export function createPageI18n(
  locale: string | undefined,
  initialI18nStore: InitialI18nStore = {}
): I18nInstance {
  const currentLocale = locale || defaultLocale;
  const translations = Object.fromEntries(
    Object.entries(initialI18nStore).flatMap(([resourceLocale, namespaces]) =>
      namespaces.translations ? [[resourceLocale, namespaces.translations]] : []
    )
  ) as I18nInstance['translations'];

  const locales = new Proxy(sharedI18n.locales, {
    get(target, property, receiver) {
      if (property === 'get') {
        return (requestedLocale?: string) => {
          const effectiveLocale = requestedLocale || currentLocale;
          const localeRule =
            target[effectiveLocale] || target[currentLocale] || target.default;
          const result =
            typeof localeRule === 'function'
              ? localeRule(effectiveLocale)
              : localeRule;
          const localeList = Array.isArray(result) ? result : [result];

          if (
            currentLocale === 'ja' &&
            effectiveLocale === 'ja' &&
            !localeList.includes('en')
          ) {
            return [...localeList, 'en'];
          }

          return localeList;
        };
      }

      return Reflect.get(target, property, receiver);
    },
  });

  const pluralization = new Proxy(sharedI18n.pluralization, {
    get(target, property, receiver) {
      if (property === 'get') {
        return (requestedLocale: string) =>
          target[requestedLocale] || target[currentLocale] || target.default;
      }

      return Reflect.get(target, property, receiver);
    },
  });

  let scopedI18n: I18nInstance;

  const callWithLocale = (
    methodName: string,
    optionsIndex: number,
    args: unknown[]
  ) => {
    const method = Reflect.get(sharedI18n, methodName) as (
      ...methodArgs: unknown[]
    ) => unknown;
    const options = args[optionsIndex];
    const optionsWithLocale =
      typeof options === 'object' && options !== null
        ? {
            ...options,
            locale: (options as { locale?: string }).locale || currentLocale,
          }
        : { locale: currentLocale };

    args[optionsIndex] = optionsWithLocale;

    return Reflect.apply(method, scopedI18n, args);
  };

  scopedI18n = new Proxy(sharedI18n, {
    get(target, property) {
      if (property === 'locale') return currentLocale;
      if (property === 'defaultLocale') return defaultLocale;
      if (property === 'translations') return translations;
      if (property === 'locales') return locales;
      if (property === 'pluralization') return pluralization;
      if (property === 'currentLocale') return () => currentLocale;

      if (property === 't' || property === 'translate') {
        return (scope: unknown, options?: unknown) =>
          callWithLocale('translate', 1, [scope, options]);
      }
      if (property === 'l' || property === 'localize') {
        return (scope: unknown, value: unknown, options?: unknown) =>
          callWithLocale('localize', 2, [scope, value, options]);
      }
      if (property === 'p' || property === 'pluralize') {
        return (count: unknown, scope: unknown, options?: unknown) =>
          callWithLocale('pluralize', 2, [count, scope, options]);
      }
      if (
        property === 'toNumber' ||
        property === 'toCurrency' ||
        property === 'toPercentage'
      ) {
        return (value: unknown, options?: unknown) =>
          callWithLocale(String(property), 1, [value, options]);
      }

      const value = Reflect.get(target, property, target);
      return typeof value === 'function' ? value.bind(scopedI18n) : value;
    },
    set(_target, property, value) {
      if (property === 'translations') {
        Object.assign(translations, value);
        return true;
      }

      return false;
    },
  });

  return scopedI18n;
}
